import dayjs, { Dayjs } from 'dayjs';
import { graphql } from 'client/gql';
import { useQuery } from 'urql';
import { useAuthClaims } from './useAuthClaims';
import { TimetableViewEntryType } from '@bp/ui-components';
import { TimetableDay } from 'components/Timetable/Timetable';

const TimetableEventsQueryDocument = graphql(`
  query TimetableEventsQueryDocumentData($profileUuid: ID!) {
    calendarEvents(where: { owner: { uuid: $profileUuid }, categories_INCLUDES: "lesson" }) {
      __typename
      uuid
      title
      start
      duration
      classShortNames
      holder {
        __typename
        ... on Group {
          __typename
          uuid
          grades
          classOrClassGroup {
            __typename
            ... on Class {
              __typename
              uuid
              color
              name
              shortName
              grade
              gradeGroup
            }
            ... on ClassGroup {
              __typename
              uuid
              name
              shortName
            }
          }
        }
      }
    }
  }
`);

export const useTimetable = () => {
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data: timetableEventsData }] = useQuery({
    query: TimetableEventsQueryDocument,
    variables: { profileUuid },
  });

  /**
   * Returns start and end times for TimetableView
   *
   * @returns object with start and end in Dayjs format
   */
  function getTimetableStartEnd(): { start: Dayjs; end: Dayjs } {
    // TODO: hardcoded for now -> get start and end from timegrid or school config?
    const timetableStart = dayjs().set('hour', 6);
    const timetableEnd = dayjs().set('hour', 18);
    return { start: timetableStart, end: timetableEnd };
  }

  /**
   * Generates an array of timetable days starting from a given date.
   * If more than one days are requested, the timetable will start from the past Monday.
   *
   * @param date - The reference date to generate timetable days from
   * @param days - The number of days to generate (1, 5, 6, 7). Defaults to 5
   * @returns Array of TimetableDay objects containing date, day of week name and array of TimetableEntries for this day
   *
   * @example
   * // Generate 5 days starting from Monday for a given date
   * const timetableDays = getTimetableDays(dayjs('2023-01-01'), 5);
   */
  function getTimetableDays(
    date: Dayjs,
    days: 1 | 5 | 6 | 7 = 5,
    onEventClick?: (uuid: string) => void,
  ): TimetableDay[] {
    const timetableDays: TimetableDay[] = [];

    const singleDay = days === 1;

    const start = singleDay ? date : date.day() === 0 ? date.subtract(1, 'day').day(1) : date.day(1);

    for (let i = 0; i < days; i++) {
      const date = start.add(i, 'days');

      const sameDayEvents =
        timetableEventsData?.calendarEvents.filter(({ start }) => dayjs(start).isSame(date, 'day')) ?? [];

      const entries: TimetableViewEntryType[] = sameDayEvents.map(
        ({ uuid, start, duration, title, classShortNames, holder }) => {
          const durationInMinutes = isNaN(Number(duration)) ? 0 : Number(duration) / 60;
          return {
            uuid: uuid,
            start: dayjs(start).toDate(),
            end: dayjs(start).add(durationInMinutes, 'minutes').toDate(),
            field1: title ?? '',
            field4: classShortNames?.join(', '),
            tooltip: {
              field1: title ?? '',
              field4: classShortNames?.join(', '),
            },
            onClick: onEventClick ? () => onEventClick(uuid) : undefined,
          };
        },
      );

      timetableDays.push({
        date,
        dayOfWeek: date.format('dddd'),
        entries,
      });
    }

    return timetableDays;
  }

  return { getTimetableStartEnd, getTimetableDays };
};
